import { Fragment } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import Loading from 'src/components/global/Loading';
import type { StatusTableRow } from './types';

type ApplicationsByStateTableProps = {
  months: string[];
  rows: StatusTableRow[];
  totalRow: StatusTableRow | null;
  loading?: boolean;
};

const headerCellStyle = {
  fontWeight: 700,
  color: '#ffffff',
  backgroundColor: '#25333d',
  borderBottom: '1px solid #9ea7ad',
  fontSize: 12,
  padding: '8px 10px',
};

const subHeaderCellStyle = {
  fontWeight: 600,
  color: '#5a5f63',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #bfc5c9',
  fontSize: 12,
  padding: '6px 10px',
};

const bodyCellStyle = {
  fontSize: 12,
  color: '#5a5f63',
  borderBottom: '1px solid #d4d7da',
  padding: '6px 10px',
};

const rightCellStyle = {
  ...bodyCellStyle,
  textAlign: 'right' as const,
};

const totalCellStyle = {
  ...bodyCellStyle,
  fontWeight: 700,
  color: '#404448',
  backgroundColor: '#fafafa',
  paddingTop: 10,
  paddingBottom: 10,
};

const totalRightCellStyle = {
  ...totalCellStyle,
  textAlign: 'right' as const,
};

export const ApplicationsByStateTable = ({
  months,
  rows,
  totalRow,
  loading = false,
}: ApplicationsByStateTableProps) => (
  <Paper
    elevation={1}
    style={{
      borderRadius: 6,
      overflow: 'hidden',
      background: '#ffffff',
    }}
  >
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={{ ...headerCellStyle, width: 130 }}>
              &nbsp;
            </TableCell>
            <TableCell colSpan={Math.max(months.length * 2, 2)} align="right" style={headerCellStyle}>
              FECHA DE DESEMBOLSO / CANTIDAD / EFECTIVIDAD
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell
              rowSpan={2}
              style={{
                ...subHeaderCellStyle,
                textAlign: 'left',
                verticalAlign: 'bottom',
                minWidth: 130,
              }}
            >
              ESTADO
            </TableCell>

            {months.map((month) => (
              <TableCell key={month} colSpan={2} align="center" style={subHeaderCellStyle}>
                {month}
              </TableCell>
            ))}
          </TableRow>

          <TableRow>
            {months.map((month) => (
              <Fragment key={month}>
                <TableCell align="right" style={subHeaderCellStyle}># Cantidad</TableCell>
                <TableCell align="right" style={subHeaderCellStyle}>% Efectividad</TableCell>
              </Fragment>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={Math.max(months.length * 2 + 1, 3)}>
                <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                  <Loading />
                </Box>
              </TableCell>
            </TableRow>
          ) : null}

          {!loading && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={Math.max(months.length * 2 + 1, 3)}>
                <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                  <Typography style={{ color: '#6b6b6b', fontSize: 14 }}>Sin informacion disponible.</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : null}

          {!loading && rows.map((row) => (
            <TableRow key={row.status}>
              <TableCell style={bodyCellStyle}>{row.status}</TableCell>
              {row.values.map((value, index) => (
                <Fragment key={`${row.status}-${index}` as string}>
                  <TableCell style={rightCellStyle}>{value.quantity}</TableCell>
                  <TableCell style={rightCellStyle}>{value.effectiveness}</TableCell>
                </Fragment>
              ))}
            </TableRow>
          ))}

          {!loading && totalRow ? (
            <TableRow>
              <TableCell style={totalCellStyle}>{totalRow.status}</TableCell>
              {totalRow.values.map((value, index) => (
                <Fragment key={`total-${index}` as string}>
                  <TableCell style={totalRightCellStyle}>{value.quantity}</TableCell>
                  <TableCell style={totalRightCellStyle}>{value.effectiveness}</TableCell>
                </Fragment>
              ))}
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);
